import { Flex } from '@chakra-ui/react';
import { FormattedNumber } from 'react-intl';
import { DEFAULT_CURRENCY } from '../../constants';
import { Header, Text } from '../../ui-components';
import { SalePrice } from '../sale-price';
import { SuggestedPrice } from '../suggested-price';
import type { Prices } from '../../bff-api/products/model';

interface Props {
    id: string;
    prices?: Prices;
    currency?: string;
    price?: number;
    size?: 'heading' | 'base';
}

export const ProductPrice: React.FC<Props> = ({
    id,
    currency = DEFAULT_CURRENCY,
    price = 0,
    size = 'heading',
    prices,
}: Props) => {
    const { regularPrice, suggestedPrice, salePrice } = prices ?? {};
    if (salePrice && (regularPrice || suggestedPrice)) {
        return (
            <Flex data-testid={id} direction="column">
                <Flex gap={2} alignItems="center">
                    <SalePrice id={id} currency={currency} price={salePrice} size={size} />
                </Flex>

                {suggestedPrice && (
                    <SuggestedPrice id={id} currency={currency} price={suggestedPrice} />
                )}

                {!suggestedPrice && regularPrice && (
                    <CrossedPrice
                        id={id}
                        currency={currency}
                        price={regularPrice}
                        size={size === 'heading' ? 'base' : 'small'}
                    />
                )}
            </Flex>
        );
    }

    return (
        <Flex data-testid={id} direction="column">
            <RegularPrice id={id} currency={currency} price={price} size={size} />

            {suggestedPrice && (
                <SuggestedPrice id={id} currency={currency} price={suggestedPrice} />
            )}
        </Flex>
    );
};

type RegularPriceProps = {
    id: string;
    currency: string;
    price: number;
    size: 'heading' | 'base';
};

const RegularPrice: React.FC<RegularPriceProps> = ({
    id,
    price = 0,
    currency = DEFAULT_CURRENCY,
    size = 'heading',
}: RegularPriceProps) => {
    if (size === 'heading') {
        return (
            <Header
                id={`${id}-product-regular-price-value`}
                as="h4"
                style="bold"
                variant="header4"
                color="slt-pepper.1000"
            >
                <FormattedNumber style="currency" currency={currency} value={price} />
            </Header>
        );
    }

    return (
        <Text id={`${id}-product-regular-price-value`} style="bold">
            <FormattedNumber style="currency" currency={currency} value={price} />
        </Text>
    );
};

type CrossedPriceProps = {
    id: string;
    currency: string;
    price: number;
    size: 'base' | 'small';
};

const CrossedPrice: React.FC<CrossedPriceProps> = ({
    id,
    price = 0,
    currency = DEFAULT_CURRENCY,
    size = 'base',
}: CrossedPriceProps) => (
    <Text
        id={`${id}-product-regular-price-value`}
        style="regular"
        type="cross"
        color="slt-black.600"
        size={size}
    >
        <FormattedNumber style="currency" currency={currency} value={price} />
    </Text>
);

ProductPrice.defaultProps = {
    size: 'heading',
    currency: DEFAULT_CURRENCY,
    price: 0,
    prices: undefined,
};
